import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { courseArt } from '@/components/course/courseArt';
import { formatMinutes } from '@/lib/activityQueries';
import type { TrainerCourse } from '@/lib/trainerQueries';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A TRAINER'S DESK
 *
 * The panel standing where Platform activity stands for an administrator.
 *
 * That chart answers "is the organisation learning more than it was", which is
 * a question a trainer can read and do nothing about. This answers the one they
 * can act on: how the courses they wrote are landing.
 *
 * Two more sat here and were removed by request — a grading queue and a "next
 * live session" card. Both are still reachable and better served elsewhere: the
 * Evaluation Queue is a standing menu entry and an attention task on this page,
 * and today's sessions are in Daily schedule in the next column, which now
 * carries its own Join now button.
 *
 * ── The card that is deliberately missing a number ────────────────────────
 * The session card's mock carried "+43 registered" over a row of avatars.
 * Nothing in this product registers anybody for a calendar event — there is no
 * attendance table at all — so that figure could only have been invented, or
 * borrowed from a count that means something else. It is not here. If it comes
 * back it needs a real attendance feature behind it, not a plausible number.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Section heading + an optional link out, shared by all three. */
function PanelHead({
  title,
  action,
}: {
  title: string;
  action?: { label: string; to: string };
}) {
  return (
    <div className="flex items-center gap-3 border-b px-4 py-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {action && (
        <Link
          to={action.to}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {action.label}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

const STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'secondary' }> = {
  PUBLISHED: { label: 'Published', variant: 'success' },
  DRAFT: { label: 'Draft', variant: 'warning' },
  ARCHIVED: { label: 'Archived', variant: 'secondary' },
};

/**
 * The completion bar.
 *
 * Renders a DASH when nobody is enrolled, never an empty bar: a draft that has
 * been given to no one is not a course failing at 0%, and an empty track reads
 * as the worst row on the panel.
 */
function Completion({ percent }: { percent: number | null }) {
  if (percent === null) {
    return <span className="text-muted-foreground" aria-label="No one enrolled yet">—</span>;
  }

  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-muted" aria-hidden>
        <span
          className="block h-full rounded-full bg-brand-600 dark:bg-brand-400"
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </span>
      <span className="font-mono text-xs tabular-nums">{percent}%</span>
    </span>
  );
}

function CourseRow({ course }: { course: TrainerCourse }) {
  const status = STATUS[course.status] ?? { label: course.status, variant: 'secondary' as const };
  const art = courseArt(course.id, course.title);

  return (
    <tr className="border-b last:border-0">
      <td className="py-2.5 pl-4 pr-3">
        <div className="flex items-center gap-3">
          {/*
            ── The course's OWN cover ────────────────────────────────────────

            An uploaded thumbnail when there is one, and otherwise `courseArt`
            — the same coloured ground and topic symbol the catalogue card, the
            builder and the assessment list all draw, keyed by the course id so
            one course wears one cover everywhere it appears.

            This used a grey book glyph tinted by CATEGORY, which meant a course
            with no category — most of them — rendered as a blank grey square
            that reads as an image that failed to load. `courseArt` never
            returns nothing: it is derived from the id and the title, so every
            course has a cover whether or not anybody uploaded one.
          */}
          {course.thumbnailUrl ? (
            <img
              src={course.thumbnailUrl}
              alt=""
              className="h-8 w-8 shrink-0 rounded-md object-cover"
            />
          ) : (
            <span
              className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md"
              style={{ background: art.color }}
              aria-hidden
            >
              {/* The same dot field the catalogue cover uses, so a small tile
                  still reads as a designed surface rather than a flat chip. */}
              <span
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    'radial-gradient(rgba(255,255,255,0.22) 1px, transparent 1px)',
                  backgroundSize: '7px 7px',
                }}
              />
              <span className="relative text-[15px] leading-none">{art.symbol}</span>
            </span>
          )}

          <span className="min-w-0">
            <Link
              to={`/admin/courses/${course.id}`}
              className="block truncate text-[13px] font-medium hover:underline"
            >
              {course.title}
            </Link>
            <span className="block text-2xs text-muted-foreground">
              {course.modules} {course.modules === 1 ? 'module' : 'modules'}
              {course.durationMinutes ? ` · ${formatMinutes(course.durationMinutes)}` : ''}
            </span>
          </span>
        </div>
      </td>

      <td className="px-3 py-2.5">
        <Badge variant={status.variant}>{status.label}</Badge>
      </td>

      <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums">
        {course.enrolled.toLocaleString('en-IN')}
      </td>

      <td className="px-3 py-2.5">
        <Completion percent={course.completionPercent} />
      </td>

      <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums">
        {course.rating === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          course.rating.toFixed(1)
        )}
      </td>

      <td className="py-2.5 pl-3 pr-4 text-right">
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/courses/${course.id}`}>Edit</Link>
        </Button>
      </td>
    </tr>
  );
}

export function MyCourses({ courses }: { courses: TrainerCourse[] }) {
  return (
    <Card className="overflow-hidden">
      <PanelHead title="My courses" action={{ label: 'View all', to: '/admin/courses' }} />

      {courses.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">
          Nothing written yet. Your courses will appear here once you create one.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-2xs uppercase tracking-[0.03em] text-muted-foreground">
                <th className="py-2 pl-4 pr-3 text-left font-medium">Course</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Enrolled</th>
                <th className="px-3 py-2 text-left font-medium">Completion</th>
                <th className="px-3 py-2 text-right font-medium">Rating</th>
                <th className="py-2 pl-3 pr-4" />
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <CourseRow key={course.id} course={course} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
